/**
 * Clientes — réplica visual del backoffice de Skeleton.
 */
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Card, Badge, Input, Button, PageHeader } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import {
  RefreshCw, Plus, Search, TrendingUp, TrendingDown, Users, ChevronRight, MoreVertical,
} from 'lucide-react'

export const Route = createFileRoute('/store/$id/customers/')({
  component: StoreCustomersPage,
})

interface CustomerListItem {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  phone: string | null
  isActive: boolean
  totalSpent: number
  avgOrderValue: number
  ordersCount: number
  paidOrdersCount: number
  cancelledCount: number
  lastOrderAt: string | null
  createdAt: string
}

interface CustomersResponse {
  data: CustomerListItem[]
  stats: {
    totalCustomers: number
    totalCustomersDeltaPct: number
    newCustomers: number
    newCustomersDeltaPct: number
    returnCustomerRate: number
    returnCustomerRateDeltaPct: number
    avgOrderRevenue: number
    avgOrderRevenueDeltaPct: number
    refunds: number
    refundsDeltaPct: number
  }
}

type Tab = 'all' | 'active'

function StoreCustomersPage() {
  const { id } = Route.useParams()
  const { activeTenantId } = useAuth()
  const [tab, setTab] = useState<Tab>('all')
  const [search, setSearch] = useState('')

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['customers', id, search],
    queryFn: () => {
      const qs = new URLSearchParams()
      qs.set('limit', '200')
      if (search) qs.set('search', search)
      return tenantApi.get<CustomersResponse>(`/api/v1/customers?${qs.toString()}`)
    },
    enabled: !!activeTenantId,
  })

  const all = data?.data ?? []
  const stats = data?.stats

  const filtered = useMemo(() => {
    if (tab === 'active') return all.filter((c) => c.isActive)
    return all
  }, [all, tab])

  return (
    <>
      <PageHeader
        title="Clientes"
        description="Compradores registrados"
        action={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isRefetching}>
              <RefreshCw className={cn('size-4', isRefetching && 'animate-spin')} />
            </Button>
            <Button size="sm" disabled title="Próxima iteración">
              <Plus className="size-4 mr-2" /> Crear
            </Button>
          </div>
        }
      />

      <div className="p-8 space-y-4">
        {/* KPIs — dos rows como en Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Kpi label="Total Customer" value={String(stats?.totalCustomers ?? 0)} change={stats?.totalCustomersDeltaPct ?? 0} />
          <Kpi label="New Customer" value={String(stats?.newCustomers ?? 0)} change={stats?.newCustomersDeltaPct ?? 0} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Kpi label="Return Customer Rate" value={`${(stats?.returnCustomerRate ?? 0).toFixed(2)}%`} change={stats?.returnCustomerRateDeltaPct ?? 0} />
          <Kpi label="Avg. Order Revenue" value={formatCop(stats?.avgOrderRevenue ?? 0)} change={stats?.avgOrderRevenueDeltaPct ?? 0} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Kpi label="Refunds" value={String(stats?.refunds ?? 0)} change={stats?.refundsDeltaPct ?? 0} invertColors />
        </div>

        {/* Tabs + Search */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setTab('all')}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition border',
                tab === 'all' ? 'bg-neutral-800 text-white border-neutral-700' : 'bg-transparent text-neutral-400 border-transparent hover:text-white',
              )}
            >
              All Customers
            </button>
            <button
              onClick={() => setTab('active')}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition border',
                tab === 'active' ? 'bg-neutral-800 text-white border-neutral-700' : 'bg-transparent text-neutral-400 border-transparent hover:text-white',
              )}
            >
              Active
            </button>
          </div>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-neutral-500" />
            <Input
              type="search"
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Customer cards */}
        {isLoading && <p className="text-sm text-neutral-500">Cargando clientes...</p>}
        {!isLoading && filtered.length === 0 && (
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <Users className="size-12 text-neutral-700 mb-3" />
            <p className="text-base font-medium">Sin clientes registrados</p>
            <p className="text-sm text-neutral-500 mt-1">Aparecerán aquí cuando se registren o hagan checkout.</p>
          </Card>
        )}

        {filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((c) => (
              <CustomerCard key={c.id} customer={c} tenantId={id} />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

function Kpi({ label, value, change, invertColors }: { label: string; value: string; change: number; invertColors?: boolean }) {
  const isUp = change >= 0
  const Icon = isUp ? TrendingUp : TrendingDown
  const color = change === 0
    ? 'text-neutral-500'
    : isUp
      ? (invertColors ? 'text-red-400' : 'text-emerald-400')
      : (invertColors ? 'text-emerald-400' : 'text-red-400')

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <p className="text-sm text-neutral-400">{label}</p>
        <span className={cn('flex items-center gap-1 text-xs font-medium', color)}>
          <Icon className="size-3" />
          {change >= 0 ? '↑' : '↓'}{Math.abs(change).toFixed(2)}%
        </span>
      </div>
      <p className="text-3xl font-bold tabular-nums mt-2">{value}</p>
    </Card>
  )
}

function CustomerCard({ customer, tenantId }: { customer: CustomerListItem; tenantId: string }) {
  const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ') || (customer.email.split('@')[0] ?? customer.email)
  const initials =
    ((customer.firstName?.[0] ?? customer.email[0] ?? '?').toUpperCase()) +
    ((customer.lastName?.[0] ?? '').toUpperCase())
  const idShort = customer.id.replace(/-/g, '').slice(0, 5)
  const lastOrderStr = customer.lastOrderAt
    ? new Date(customer.lastOrderAt).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—'

  return (
    <Card className="p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="size-10 rounded-md bg-neutral-800 border border-neutral-700 flex items-center justify-center text-sm font-bold uppercase shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{fullName}</p>
          <p className="text-xs text-neutral-500 mt-0.5 font-mono">ID #{idShort}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={customer.isActive ? 'success' : 'muted'}>
            {customer.isActive ? 'Active' : 'Inactive'}
          </Badge>
          <button className="p-1 rounded text-neutral-500 hover:text-white hover:bg-neutral-800">
            <MoreVertical className="size-4" />
          </button>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <Row label="Total Order" value={formatCop(customer.totalSpent)} />
        <Row label="Avg. Order Value" value={formatCop(customer.avgOrderValue)} />
        <Row label="No. of Order" value={String(customer.ordersCount)} />
        <Row label="Last Order" value={lastOrderStr} />
      </div>

      <Link
        to="/store/$id/customers/$customerId"
        params={{ id: tenantId, customerId: customer.id }}
        className="mt-4 -mx-5 -mb-5 px-5 py-3 border-t border-neutral-800 flex items-center justify-between text-sm hover:bg-neutral-800/40 transition"
      >
        <span>Ver detalles</span>
        <ChevronRight className="size-4 text-neutral-500" />
      </Link>
    </Card>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-neutral-400">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  )
}

function formatCop(amount: number): string {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(amount / 100)
}
